import { Section } from "@/components/Section/Section";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { Modul_Kontakt } from "@/wp/types/acf-module-types";
import { ContentTag } from "@/components/ContentTag/ContentTag";
// import { Button } from "@/components/Button/Button";
import { ExtendedLink } from "@/components/ExtededLink/ExtendedLink";
import styles from "./Contact.module.scss";
import { ContactOptionsType } from "@/types/ModuleOptionsType";
// import { MediaItem } from "@/components/MediaItem/MediaItem";
import { ImageType } from "@/types/baseTypes";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { customSanitize } from "@/utils/customSanitize";

export type ContactModuleType = Modul_Kontakt;

interface ContactProps {
  data: ContactModuleType;
  contactOptions: ContactOptionsType;
  contactData: ContactTeamData;
}

export interface ContactTeamData {
  image: ImageType | false;
  name: string;
  position: string;
  firma: string;
}

const QuoteIcon = () => {
  const { getIcon } = useGlobalContext();
  return (
    <div className={styles["quote-icon"]}>
      <CustomSVG
        width={50}
        height={31}
        src={getIcon("quote")}
        wrapper={"span"}
        alt="quote"
      />
    </div>
  );
};

const Contact = ({ data, contactOptions, contactData }: ContactProps) => {
  const {
    id,
    background_color_class_with_image,
    background_image,
    section_margin_class,
    text_size,
  } = data.layout;
  const {
    button_type,
    show_extended_link,
    box,
    box_background_color,
    box_opacity,
    image_as_bg,
    row_direction_class,
    text_color,
  } = contactOptions;

  const { content_col, button_col } = data.contact;

  {
    /* jscpd:ignore-start */
  }
  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }
  {
    /* jscpd:ignore-end */
  }

  // @ts-expect-error: background_color_class_with_image might not be in the type definition
  const hasImage = background_color_class_with_image === "has-image-bg";

  const bgStyle =
    image_as_bg && background_image && hasImage
      ? {
          backgroundImage: `url(${background_image.url})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }
      : {};

  const getBoxPadding = (box_padding: typeof box.box_padding) => {
    return {
      "--box-padding-mobile": box_padding.mobile.trim() || "1.5rem",
      "--box-padding-tablet": box_padding.tablet.trim() || "2rem",
      "--box-padding-desktop": box_padding.desktop.trim() || "2.5rem",
      "--box-padding-fullhd": box_padding.fullhd.trim() || "3rem",
    };
  };

  const showBoxClasses =
    box_background_color || box_opacity || box.show_box_shadow
      ? `${box.show_border_radius ? "has-border-radius" : ""} ${
          box.show_box_shadow ? "has-box-shadow" : ""
        }`
      : "";

  const boxStyle = {
    ...(getBoxPadding(box.box_padding) as React.CSSProperties),
    ...(box_opacity && image_as_bg && hasImage
      ? { "--opacity": box_opacity }
      : {}),
    ...(box_background_color && image_as_bg && hasImage
      ? { "--background-color": `var(--${box_background_color})` }
      : {}),
    ...(text_color && image_as_bg && hasImage
      ? { "--text-color": `var(--${text_color})` }
      : {}),
  } as React.CSSProperties;

  const { name, firma } =
    content_col.type === "team" ? contactData : content_col;
  return (
    <Section
      className={`${styles["contact"]} ${id ?? ""} ${background_color_class_with_image} ${section_margin_class}`}
      columnsClassName="is-centered"
      id={id}
      style={bgStyle}
    >
      <div
        style={boxStyle}
        className={`column is-12 ${showBoxClasses} box-wrapper`}
      >
        {/* please change the row_direction_class when backend option is updated*/}
        <div
          className={`columns is-multiline ${row_direction_class === "is-row-normal-desktop" ? "is-row-reverse-desktop" : "is-row-normal-desktop"} ${styles["contact-columns"]}`}
        >
          {/* Content Column (Left - Quote Section) */}
          <div className="column is-12 is-7-desktop is-flex">
            <div
              className={`is-flex-grow-1 is-box ${styles["content-col-wrapper"]}`}
            >
              <div className={styles["content-col-inner"]}>
                {/* {image && (
                  <div className="mb-4 is-flex is-justify-content-center">
                    <MediaItem
                      className="profile-image"
                      image_desktop={image}
                      sizes=""
                    />
                  </div>
                )} */}
                <div className={styles["content-col-text"]}>
                  {content_col.headline && (
                    <span className="headline">{content_col.headline}</span>
                  )}
                  {content_col.content && (
                    <div className="content">
                      <ContentTag
                        content={content_col.content}
                        extraClass={`${
                          text_size === "big-text"
                            ? "content-text-big"
                            : "content-text-small"
                        } contact-text-color ${styles["content"]}`}
                      />
                    </div>
                  )}
                  {(name || firma) && (
                    <div
                      className={`${styles["contact-author"]} pb-0 ${
                        text_size === "big-text"
                          ? "content-text-big"
                          : "content-text-small"
                      }`}
                    >
                      {name && (
                        <p
                          className={`contact-name 
                           is-block mb-0 pb-0 contact-text-color`}
                          dangerouslySetInnerHTML={{
                            __html: customSanitize(
                              `${name} ${firma ? `, ${firma}` : ""}`,
                            ),
                          }}
                        ></p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <QuoteIcon />
            </div>
          </div>
          {/* Button Column (Right) */}
          <div className="column is-12 is-5-desktop is-flex">
            <div className={`is-flex-grow-1 ${styles["button-col-wrapper"]}`}>
              {button_col.headline && (
                <span className={`headline ${styles["button-col-headline"]}`}>
                  {button_col.headline}
                </span>
              )}
              {button_col.content && (
                <div className={`content ${styles["button-col-content"]}`}>
                  <ContentTag
                    extraClass={`${
                      text_size === "big-text"
                        ? "content-text-big"
                        : "content-text-small"
                    } ${styles["button-col-text"]}`}
                    content={button_col.content}
                  />
                </div>
              )}

              <div className={styles["button-col-btn"]}>
                {/* {button_col.link && button_col.link.url && (
                  <Button
                    type={button_type}
                    href={button_col.link.url}
                    target={button_col.link.target || "_self"}
                    className="mb-0"
                  >
                    {button_col.link.title}
                  </Button>
                )} */}

                {show_extended_link && button_col.link_extended && (
                  <ExtendedLink
                    extendedLink={button_col.link_extended}
                    btnType={button_type}
                    extraClass="mt-0"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Contact;
